import { Stack, Typography } from "@mui/material";
import { CommentThreadFragment } from "generated-graphql/graphql";

import { CommentInput } from "./CommentInput";
import { CommentThread } from "./CommentThread";

interface GenericCommentStreamProps {
  title?: string;
  comments: CommentThreadFragment[];
  currentUserId?: string;
  onAddComment: (message: string) => Promise<void | { clear: boolean }>;
  onDeleteComment?: (commentId: string) => Promise<void>;
  addLoading?: boolean;
  deleteLoading?: boolean;
  emptyStateMessage?: string;
  placeholder?: string;
  buttonText?: string;
  showDeleteButton?: boolean;
  disabled?: boolean;
  submitButtonVariant?: "text" | "icon";
  inputRows?: number;
  inputMaxRows?: number;
  /** See CommentInput: hide controls until focused or non-empty. */
  collapseControlsWhenEmpty?: boolean;
  /** See CommentInput: rendered right-aligned, just before the submit button. */
  footerEnd?: React.ReactNode;
  /** See CommentInput: rendered inline at the start of the field. */
  inputStartAdornment?: React.ReactNode;
  /** Rendered directly under each comment's body. Used for attachment chips. */
  renderCommentFooter?: (comment: CommentThreadFragment) => React.ReactNode;
  /** See CommentThread: rendered between the author/date line and the body. */
  renderCommentSubheader?: (comment: CommentThreadFragment) => React.ReactNode;
}

/**
 * Generic comment stream component, any entity that has a comment with @Prisma.Comment can use this component
 *
 * @example
 * ```tsx
 * const { addComment, loading: addLoading } = useAddMyEntityComment({ entityId });
 * const { deleteComment, loading: deleteLoading } = useDeleteMyEntityComment({ commentId });
 *
 * <GenericCommentStream
 *   title="Entity Comments"
 *   comments={entity.comments}
 *   currentUserId={currentUser.id}
 *   onAddComment={addComment}
 *   onDeleteComment={deleteComment}
 *   addLoading={addLoading}
 *   deleteLoading={deleteLoading}
 *   placeholder="Add a comment to this entity..."
 *   buttonText="Comment"
 * />
 * ```
 */
export function GenericCommentStream({
  title = "Comments",
  comments,
  currentUserId,
  onAddComment,
  onDeleteComment,
  addLoading = false,
  deleteLoading = false,
  emptyStateMessage = "No comments yet.",
  placeholder = "Add a comment...",
  buttonText = "Add Comment",
  showDeleteButton = true,
  disabled = false,
  submitButtonVariant = "text",
  inputRows = 1,
  inputMaxRows = 8,
  collapseControlsWhenEmpty = false,
  footerEnd,
  inputStartAdornment,
  renderCommentFooter,
  renderCommentSubheader,
}: GenericCommentStreamProps) {
  const streamSpacing = title ? 1 : 1.75;

  return (
    <Stack spacing={streamSpacing}>
      {title ? (
        <Typography variant="caption" color="text.secondary">
          {title}
        </Typography>
      ) : null}

      {/* Display existing comments section */}
      <CommentThread
        comments={comments}
        currentUserId={currentUserId}
        onDeleteComment={onDeleteComment}
        deleteLoading={deleteLoading}
        emptyStateMessage={emptyStateMessage}
        showDeleteButton={showDeleteButton}
        renderCommentFooter={renderCommentFooter}
        renderCommentSubheader={renderCommentSubheader}
      />

      {/* Add new comment section */}
      <CommentInput
        onSubmit={onAddComment}
        loading={addLoading}
        placeholder={placeholder}
        buttonText={buttonText}
        rows={inputRows}
        maxRows={inputMaxRows}
        disabled={disabled}
        submitButtonVariant={submitButtonVariant}
        collapseControlsWhenEmpty={collapseControlsWhenEmpty}
        footerEnd={footerEnd}
        startAdornment={inputStartAdornment}
      />
    </Stack>
  );
}
